class RecipeHelpers {
  gcd(a, b) {
    return b === 0 ? a : this.gcd(b, a % b);
  }

  toFraction(decimal) {
    // handle integers
    if (decimal % 1 === 0) return decimal.toString();
    // use continued fraction to get simple rational approximation
    const tolerance = 1e-6;
    let h1 = 1,
      h2 = 0,
      k1 = 0,
      k2 = 1;
    let b = decimal;
    let a, h, k;
    while (true) {
      a = Math.floor(b);
      h = a * h1 + h2;
      k = a * k1 + k2;
      if (k !== 0 && Math.abs(decimal - h / k) < tolerance) {
        return k === 1 ? `${h}` : `${h}/${k}`;
      }
      h2 = h1;
      h1 = h;
      k2 = k1;
      k1 = k;
      b = 1 / (b - a);
    }
  }

  scaleAmount(amountStr, factor) {
    amountStr = amountStr.trim();
    // Mixed number like '1 1/2'
    const mixedMatch = amountStr.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1]);
      const num = parseInt(mixedMatch[2]);
      const den = parseInt(mixedMatch[3]);
      const total = whole + num / den;
      const scaled = total * factor;
      return this.toFraction(scaled);
    }
    // Fraction '1/2'
    const fracMatch = amountStr.match(/^(\d+)\/(\d+)$/);
    if (fracMatch) {
      const num = parseInt(fracMatch[1]);
      const den = parseInt(fracMatch[2]);
      const scaled = (num / den) * factor;
      return this.toFraction(scaled);
    }
    // Number
    const num = parseFloat(amountStr);
    if (!isNaN(num)) {
      const scaled = num * factor;
      return this.toFraction(scaled);
    }
    return amountStr;
  }

  // Format decimal with comma separator, rounded to 2 decimals
  formatDecimal(num) {
    return num.toFixed(2).replace('.', ',').replace(/,?0+$/, '');
  }

  // Unit conversions
  convertUnit(amount, unit, targetSystem) {
    // Parse amount to number
    const numAmount = this.parseAmountToNumber(amount);
    if (isNaN(numAmount)) return { amount, unit };
    if (unit.charAt(unit.length - 1) === '.') unit = unit.trim().slice(0, -1); // remove trailing dot from units like 'tsp.'

    const conversions = {
      // To metric
      metric: {
        'tsp': { amount: numAmount * 4.92892, unit: 'ml' },
        'tbsp': { amount: numAmount * 14.7868, unit: 'ml' },
        'fl oz': { amount: numAmount * 29.5735, unit: 'ml' },
        'cup': { amount: numAmount * 236.588, unit: 'ml' },
        'pt': { amount: numAmount * 473.176, unit: 'ml' },
        'qt': { amount: numAmount * 946.353, unit: 'ml' },
        'gal': { amount: numAmount * 3785.41, unit: 'ml' },
        'oz': { amount: numAmount * 28.3495, unit: 'g' },
        'lb': { amount: numAmount * 453.592, unit: 'g' },
        'ml': { amount: numAmount, unit: 'ml' },
        'l': { amount: numAmount, unit: 'l' },
        'mg': { amount: numAmount, unit: 'mg' },
        'g': { amount: numAmount, unit: 'g' },
        'kg': { amount: numAmount, unit: 'kg' },
      },
      // To imperial
      imperial: {
        'ml': { amount: numAmount < 15 ? numAmount / 4.92892 : numAmount < 50 ? numAmount / 14.7868 : numAmount / 29.5735, unit: numAmount < 15 ? 'tsp' : numAmount < 50 ? 'tbsp' : 'fl oz' },
        'l': { amount: numAmount * 1000 / 29.5735, unit: 'fl oz' }, // approx
        'g': { amount: numAmount < 30 ? numAmount / 28.3495 : numAmount / 453.592, unit: numAmount < 30 ? 'oz' : 'lb' },
        'kg': { amount: numAmount * 1000 / 28.3495, unit: 'oz' }, // approx
        'tsp': { amount: numAmount, unit: 'tsp' },
        'tbsp': { amount: numAmount, unit: 'tbsp' },
        'fl oz': { amount: numAmount, unit: 'fl oz' },
        'cup': { amount: numAmount, unit: 'cup' },
        'pt': { amount: numAmount, unit: 'pt' },
        'qt': { amount: numAmount, unit: 'qt' },
        'gal': { amount: numAmount, unit: 'gal' },
        'oz': { amount: numAmount, unit: 'oz' },
        'lb': { amount: numAmount, unit: 'lb' },
      }
    };

    const conversion = conversions[targetSystem][unit.toLowerCase()];
    if (conversion) {
      // For metric system, use decimal format with comma; for imperial, use fractions
      const formattedAmount = targetSystem === 'metric' 
        ? this.formatDecimal(conversion.amount) 
        : this.toFraction(conversion.amount);
      return { amount: formattedAmount, unit: conversion.unit };
    }
    return { amount, unit };
  }

  parseAmountToNumber(amountStr) {
    amountStr = amountStr.trim();
    const mixedMatch = amountStr.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1]);
      const num = parseInt(mixedMatch[2]);
      const den = parseInt(mixedMatch[3]);
      return whole + num / den;
    }
    const fracMatch = amountStr.match(/^(\d+)\/(\d+)$/);
    if (fracMatch) {
      const num = parseInt(fracMatch[1]);
      const den = parseInt(fracMatch[2]);
      return num / den;
    }
    const num = parseFloat(amountStr);
    return isNaN(num) ? NaN : num;
  }
}

export default new RecipeHelpers();